/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Lightmetrics Pooler API
 * This API handles the listing of two kind of videos: events and footages. Video events are automatically triggered by the dashcam on the vehicle whenever an event of interest happens.
 * Video footages can be requested given an arbitrary timestamp and duration of a valid vehicle trip. It can take significant time for the request to complete, since the dashcam needs to be online to upload the requested footage.
 * OpenAPI spec version: v1.0.0
 */
import * as zod from 'zod/v4';

export const Unit = zod.looseObject({});

export type Unit = zod.input<typeof Unit>;
export type UnitOutput = zod.output<typeof Unit>;

export const FavoriteMediaRequestDTO = zod.object({
  is_favorite: zod.boolean().describe('Identifies whether the user wants to favorite and unfavorite the media'),
});

export type FavoriteMediaRequestDTO = zod.input<typeof FavoriteMediaRequestDTO>;
export type FavoriteMediaRequestDTOOutput = zod.output<typeof FavoriteMediaRequestDTO>;

export const UserFavoriteMediaResponseDTO = zod.object({
  is_favorite: zod.boolean().describe('The current state of the media after the update'),
  media_id: zod.uuid().describe('The media that was modified'),
});

export type UserFavoriteMediaResponseDTO = zod.input<typeof UserFavoriteMediaResponseDTO>;
export type UserFavoriteMediaResponseDTOOutput = zod.output<typeof UserFavoriteMediaResponseDTO>;

export const CreateMediaCommentRequest = zod.object({
  created_by: zod.uuid(),
  media_id: zod.uuid(),
  content: zod.string(),
});

export type CreateMediaCommentRequest = zod.input<typeof CreateMediaCommentRequest>;
export type CreateMediaCommentRequestOutput = zod.output<typeof CreateMediaCommentRequest>;

export const MediaComment = zod
  .object({
    id: zod.uuid().describe('Media comment ID'),
    media_id: zod.uuid().describe('Media ID associated with comment'),
    created_by: zod.uuid().describe('ID of the user that created the comment'),
    content: zod.string().nullish().describe('Content of the comment'),
    created_at: zod.iso.datetime({ offset: true }).describe('Datetime of comment creation'),
    updated_at: zod.iso.datetime({ offset: true }).nullish().describe('Datetime of comment last update'),
  })
  .describe('Comments for the media');

export type MediaComment = zod.input<typeof MediaComment>;
export type MediaCommentOutput = zod.output<typeof MediaComment>;

export const CreateSharedMediaDTO = zod.object({
  media_id: zod.uuid().describe('Media identifier in the UUID format'),
});

export type CreateSharedMediaDTO = zod.input<typeof CreateSharedMediaDTO>;
export type CreateSharedMediaDTOOutput = zod.output<typeof CreateSharedMediaDTO>;

export const CreateSharedMediaResponseDTO = zod.object({
  sharable_media_url: zod.string().describe('Sharable media URL'),
  expiration_timestamp: zod.int().describe('Url expiration Unix timestamp in millis.'),
});

export type CreateSharedMediaResponseDTO = zod.input<typeof CreateSharedMediaResponseDTO>;
export type CreateSharedMediaResponseDTOOutput = zod.output<typeof CreateSharedMediaResponseDTO>;

export const UpdateMediaCommentRequest = zod.object({
  created_by: zod.uuid(),
  media_id: zod.uuid(),
  comment_id: zod.uuid(),
  content: zod.string(),
});

export type UpdateMediaCommentRequest = zod.input<typeof UpdateMediaCommentRequest>;
export type UpdateMediaCommentRequestOutput = zod.output<typeof UpdateMediaCommentRequest>;

export const SafetyOccurrence = zod
  .object({
    id: zod.uuid().describe('Safety occurrence ID'),
    name: zod.string().describe('Occurrence label name'),
  })
  .describe('Type of registered occurrence');

export type SafetyOccurrence = zod.input<typeof SafetyOccurrence>;
export type SafetyOccurrenceOutput = zod.output<typeof SafetyOccurrence>;

export const SafetyReview = zod.object({
  id: zod.uuid().describe('Safety review ID'),
  fleet_id: zod.uuid().describe('Fleet ID'),
  status: zod
    .union([zod.literal('RELEVANT'), zod.literal('DISMISSED'), zod.literal('ARCHIVED'), zod.literal(null)])
    .nullish()
    .describe('Current safety review status'),
  driver_id: zod.uuid().nullish().describe('Driver ID associated with review'),
  original_media_driver_id: zod.uuid().nullish().describe("Media's Driver ID before review was created"),
  severity: zod
    .union([zod.literal('LOW'), zod.literal('MEDIUM'), zod.literal('HIGH'), zod.literal('HIGHER'), zod.literal(null)])
    .nullish()
    .describe('Severity associated with review'),
  original_media_severity: zod
    .union([zod.literal('LOW'), zod.literal('MEDIUM'), zod.literal('HIGH'), zod.literal('HIGHER'), zod.literal(null)])
    .nullish()
    .describe("Media's severity before review was created"),
  dismiss_reason: zod
    .union([
      zod.literal('NO_EVENT_OR_OCCURRENCE'),
      zod.literal('WRONG_EVENT_OR_OCCURRENCE'),
      zod.literal('BLURRY_OR_LOW_QUALITY'),
      zod.literal('EVENT_NOT_INTERESTING'),
      zod.literal(null),
    ])
    .nullish()
    .describe('Reason used on dismissing'),
  created_by: zod.uuid().describe('ID of the user that first reviewed'),
  updated_by: zod.uuid().describe('ID of the user that last updated'),
  created_at: zod.iso.datetime({ offset: true }).nullish().describe('Datetime of review creation'),
  updated_at: zod.iso.datetime({ offset: true }).nullish().describe('Datetime of review last update'),
  occurrences: zod.array(SafetyOccurrence).describe('Associated occurrences'),
  archived_at: zod.iso.datetime({ offset: true }).nullish().describe('Datetime the review was archived. Null when not archived'),
  archived_by: zod.uuid().nullish().describe('ID of the user that archived'),
  archived_by_name: zod.string().nullish().describe('Name of the user that archived'),
  archived_origin: zod
    .union([zod.literal('PANEL'), zod.literal('API'), zod.literal(null)])
    .nullish()
    .describe('Where the archive came from'),
});

export type SafetyReview = zod.input<typeof SafetyReview>;
export type SafetyReviewOutput = zod.output<typeof SafetyReview>;
/**
 * The user has a chance to ask for a manual retry for a media request
 * @summary Ask for manual retry for media request
 */
export const RetryMediaRequestParams = zod.object({
  mediaId: zod.uuid(),
});

export const RetryMediaRequestResponse = Unit;

/**
 * The specific media will be the favorite for the user user in that fleet
 * @summary Favorite or unfavorite a media
 */
export const CreateOrUpdateFavoriteMediaParams = zod.object({
  mediaId: zod.uuid(),
});

export const CreateOrUpdateFavoriteMediaBody = FavoriteMediaRequestDTO;

export const CreateOrUpdateFavoriteMediaResponse = UserFavoriteMediaResponseDTO;

/**
 * If the requester is authorized to create a comment for the media, a comment will be created.
 * @summary Create a comment for a media
 */
export const CreateMediaCommentParams = zod.object({
  mediaId: zod.uuid(),
});

export const CreateMediaCommentBody = CreateMediaCommentRequest;

export const CreateMediaCommentResponse = MediaComment;

/**
 * If the requester is authorized to see the media, it will return a generated sharable media URL and its expiration date.
 * @summary Used to create sharable a media.
 */
export const CreateSharedMediaBody = CreateSharedMediaDTO;

export const CreateSharedMediaResponse = CreateSharedMediaResponseDTO;

/**
 * If the requester is authorized to delete a comment for the media, a comment will be deleted.
 * @summary Delete a comment given mediaId and commentId
 */
export const DeleteMediaCommentParams = zod.object({
  mediaId: zod.uuid(),
  commentId: zod.uuid(),
});

export const DeleteMediaCommentResponse = zod.unknown();

/**
 * If the requester is authorized to update a comment for the media, a comment will be updated.
 * @summary Update a comment given mediaId and commentId
 */
export const UpdateMediaCommentParams = zod.object({
  mediaId: zod.uuid(),
  commentId: zod.uuid(),
});

export const UpdateMediaCommentBody = UpdateMediaCommentRequest;

export const UpdateMediaCommentResponse = MediaComment;

/**
 * Takes the video back to not reviewed, keeping driver, occurrences and severity. A review that isn't archived is returned unchanged; 204 when the media has no review
 * @summary Unarchives a Safety Review
 */
export const UnarchiveSafetyReviewParams = zod.object({
  mediaId: zod.uuid(),
});

export const UnarchiveSafetyReviewResponse = SafetyReview;
